import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2 } from 'lucide-react';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { useSystemStripeMode } from '@/hooks/useSystemStripeMode';

interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  price_annual: number;
  price_lifetime: number;
  stripe_price_monthly_id: string | null;
  stripe_price_annual_id: string | null;
  stripe_price_lifetime_id: string | null;
}

interface PlanPaymentSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: SubscriptionPlan;
}

type BillingPeriod = 'monthly' | 'annual' | 'lifetime';

export function PlanPaymentSelector({ open, onOpenChange, plan }: PlanPaymentSelectorProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>('monthly');
  const { initiateCheckout, isProcessing } = useStripeCheckout();
  const { isTestMode, isProductionMode } = useSystemStripeMode();

  const calculateSavings = (monthly: number, annual: number) => {
    const monthlyCost = monthly * 12;
    return monthlyCost - annual;
  };

  const availablePeriods = [
    {
      value: 'monthly',
      label: 'Mensal',
      price: plan.price_monthly,
      priceId: plan.stripe_price_monthly_id,
      description: 'Renovação automática mensal',
    },
    {
      value: 'annual',
      label: 'Anual',
      price: plan.price_annual,
      priceId: plan.stripe_price_annual_id,
      savings: calculateSavings(plan.price_monthly, plan.price_annual),
      description: 'Renovação automática anual',
    },
    {
      value: 'lifetime',
      label: 'Vitalício',
      price: plan.price_lifetime,
      priceId: plan.stripe_price_lifetime_id,
      description: 'Pagamento único, acesso vitalício',
    },
  ].filter(period => period.price > 0 && period.priceId);

  const handleProceedToPayment = async () => {
    await initiateCheckout(plan.id, selectedPeriod);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Escolha o período de pagamento</DialogTitle>
          <DialogDescription>
            Selecione como deseja pagar pelo plano {plan.name}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={selectedPeriod}
            onValueChange={(value) => setSelectedPeriod(value as BillingPeriod)}
            className="space-y-3"
          >
            {availablePeriods.map((period) => (
              <div
                key={period.value}
                className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => setSelectedPeriod(period.value as BillingPeriod)}
              >
                <RadioGroupItem value={period.value} id={period.value} />
                <Label
                  htmlFor={period.value}
                  className="flex-1 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{period.label}</div>
                      <div className="text-sm text-muted-foreground">
                        {period.description}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg">
                        R$ {period.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                      {period.savings && period.savings > 0 && (
                        <div className="text-sm text-green-600 flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          Economize R$ {period.savings.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                  </div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
          <div className="flex-1 text-left">
            {isTestMode && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  🧪 MODO TESTE
                </Badge>
                <p className="text-xs text-muted-foreground">
                  Página abrirá em nova aba
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleProceedToPayment}
              disabled={isProcessing}
              className="min-w-[200px]"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                'Prosseguir para pagamento'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
